import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { AudioCategory } from '../../../audio-preview/core/audioCategory';
import { PeaksMotion } from '../../../audio-preview/core/peaksMotion';
import { SpectrumFrame } from '../../../audio-preview/core/spectrumFeatures';
import { usePrefersReducedMotion } from '../../../../hooks/usePrefersReducedMotion';
import {
  AudioDriver,
  DriveSignal,
  MOTION_PRESETS,
  createAudioDriver,
  createSpectrumDriver,
} from './audioDriver';
import { createAudioThumbnailPalette, rgba } from './audioThumbnailPalette';
import {
  MESH_CANVAS_SIZE,
  createMeshScene,
  registerMeshTicker,
  renderMeshFrame,
} from './meshGradientRenderer';

/**
 * サムネイル画像を持たないオーディオの生成サムネイル。
 *
 * 白基調のガラス板を地とし、その内側で淡い色の雲が滑らかに動く。色は全面を
 * 埋めず、円形マスクで縁を白へ抜くことで「板の中に色が入っている」見え方に
 * する。カテゴリが動きのプリセットと基本色を、波形 peaks 由来のエンベロープが
 * 駆動信号を、シードが配色と配置の個体差をそれぞれ決める。
 *
 * 「磨りガラス」に見せないため、面を覆う白と拡散（粒・ボケ）は最小限に留め、
 * 板であることは縁の屈折で示す。表面を勝手に動く光は置かない。動くのは中の
 * 色だけで、面が光るのはユーザーが触れたとき（ホバーの glare）と再生中の
 * 内側からのグローだけにしている。
 */

/** グローを光らせる条件。動かしながら調整できるよう prop で切り替える */
export type AudioThumbnailGlowTrigger = 'hover' | 'playing' | 'both' | 'always' | 'none';

/** メッシュは高フレームレートを必要としない。30fps に間引いて負荷を半減させる */
const MIN_FRAME_INTERVAL_MS = 1000 / 30;

/** グローの基準不透明度 */
const GLOW_ALPHA_ACTIVE = 1;
const GLOW_ALPHA_IDLE = 0.18;

/**
 * グレアの不透明度。ポインタが中央にあるときは弱く、縁へ寄るほど強い。
 * 光を斜めから受けたガラスのほうが強く光る、という見え方に合わせている。
 */
const GLARE_OPACITY_CENTER = 0.2;
const GLARE_OPACITY_EDGE = 0.58;

/**
 * フィルムグレイン。参考ビジュアル同様の粒状感を出すと同時に、淡い階調で
 * 目立ちやすいバンディングをディザリングで隠す役割も持つ。
 * feTurbulence を使えば画像アセットを増やさずタイル可能なノイズが作れる。
 *
 * feTurbulence はアルファにもノイズを出すため、feFuncA でアルファを 1 に
 * 固定しないと実効コントラストが半減して粒が見えない。そのうえで gamma で
 * 全体を明るく寄せる（multiply で乗せるので、暗いままだと板が灰色に沈む）。
 */
const GRAIN_TILE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='g' color-interpolation-filters='sRGB'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3CfeComponentTransfer%3E%3CfeFuncR type='gamma' exponent='0.5'/%3E%3CfeFuncG type='gamma' exponent='0.5'/%3E%3CfeFuncB type='gamma' exponent='0.5'/%3E%3CfeFuncA type='linear' slope='0' intercept='1'/%3E%3C/feComponentTransfer%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23g)'/%3E%3C/svg%3E\")";

export interface AudioThumbnailCanvasProps {
  category: AudioCategory;
  /** 配色と配置を決める種。ファイル ID など個体で一意な文字列 */
  seed: string;
  /** peaks 由来のエンベロープと BPM。未取得なら null（自律ドリフトのみになる） */
  motion: PeaksMotion | null;
  isPlaying: boolean;
  isHovering: boolean;
  glowTrigger?: AudioThumbnailGlowTrigger;
  /** 再生位置（秒）を返す。毎フレーム呼ぶので再レンダリングを挟まない形で渡す */
  getCurrentTime: () => number;
  /**
   * 再生中トラックの実スペクトル。値が返る間はこちらで駆動する。
   * 解析が張れていない（CORS 不可・ミュート中など）ときは null が返る。
   */
  readSpectrum?: () => SpectrumFrame | null;
  /**
   * 画面内に初めて入ったときに一度だけ呼ぶ。音源の分類は取得を伴うので、
   * 見えているものから順に走らせるために使う。
   */
  onFirstVisible?: () => void;
}

// =============================================================================
// Styled Components
// =============================================================================

/**
 * ガラス板の地。ほぼ白だが、カテゴリの色相をごく僅かに含む surface を敷くことで
 * 無彩色の白より「色が透けている板」に見える。
 * mix-blend-mode を使う子を含むため isolation で合成範囲を閉じる。
 */
const Root = styled.div<{ $surface: string }>`
  position: absolute;
  inset: 0;
  overflow: hidden;
  isolation: isolate;
  border-radius: inherit;
  background: ${props => props.$surface};
`;

/**
 * 色の雲。キャンバス側で円形マスクを掛けて縁を透明に抜いているため、
 * blur が縁を溶かしても白地へなじむ。親と同じ大きさで置いてよい。
 */
const MeshCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* 拡大時のガタを消す最小限だけ。ボケは光の拡散、つまり磨りガラスの記号なので
     かけるほど曇る。内部解像度を上げてここを浅くするのが透明感の要 */
  filter: blur(1px);
  pointer-events: none;
`;

/**
 * ごく薄い粒。質感ではなくディザリングとして置いている。
 * 淡いグラデーションは 8bit だと帯（バンディング）が出るので、それを崩す
 * 最小量だけ。上げると途端に磨りガラスに見える。
 */
const GrainLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: ${GRAIN_TILE};
  background-repeat: repeat;
  mix-blend-mode: multiply;
  opacity: 0.03;
`;

/**
 * ガラス板の稜線。
 *
 * 面を覆う白は「曇り」に直結するので、広いグラデーションはほぼ置かない。
 * 板であることは上下辺の 1px の稜線だけで示す。角の映り込みも狭く強くする
 * （広く弱いと途端に霞んで見える）。
 */
const GlassLayer = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 1),
    inset 0 -1px 0 rgba(255, 255, 255, 0.75);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background:
      radial-gradient(
        ellipse 28% 12% at 20% 1%,
        rgba(255, 255, 255, 0.95) 0%,
        rgba(255, 255, 255, 0) 70%
      ),
      radial-gradient(
        ellipse 34% 12% at 80% 100%,
        rgba(255, 255, 255, 0.55) 0%,
        rgba(255, 255, 255, 0) 72%
      );
  }
`;

/**
 * 縁の屈折。板の「切り口」を作る層。
 *
 * ガラス板を上から見ると、縁の数 px だけは光路が曲がって内側の色が濃く・
 * 明るく圧縮されて見える。backdrop-filter を縁のリングにだけ掛けると、
 * 実際に背後（色の雲）を再処理するのでこれをそのまま再現できる。
 * 表面に白を足さずに「透明な厚みのある板」を示せるのが利点。
 *
 * padding + mask-composite でリング状に切り抜くのは EdgeLayer と同じ定石。
 */
const EdgeRefractionLayer = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  padding: 4px;
  backdrop-filter: saturate(1.9) brightness(1.05);
  -webkit-backdrop-filter: saturate(1.9) brightness(1.05);
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  mask-composite: exclude;
`;

/**
 * 縁のダイクロイック。ガラスの切り口が光を色ごとに分けている見え方を狙って、
 * 1px の枠だけを色相の違う 2 色のグラデーションで塗る。
 * padding + mask-composite で「枠の部分だけ残す」のが定石。
 */
const EdgeLayer = styled.div<{ $from: string; $to: string }>`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  padding: 1px;
  background: linear-gradient(
    135deg,
    ${props => props.$from} 0%,
    rgba(255, 255, 255, 0.92) 42%,
    ${props => props.$to} 100%
  );
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  mask-composite: exclude;
`;

/**
 * ソフトグロー。外側の box-shadow は親の overflow: hidden で切られるため、
 * 内側から縁が光る inset shadow で表現する。
 * 白い内側の輝きが色を中央へ押し込むので、光るほど白基調が強まる。
 * 強さは再生中のエネルギー（--audio-thumb-energy）で脈打つ。
 */
const GlowLayer = styled.div<{ $glow: string; $glowSoft: string; $alpha: number }>`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow:
    inset 0 0 6px rgba(255, 255, 255, 0.6),
    inset 0 0 26px ${props => props.$glow},
    inset 0 0 52px ${props => props.$glowSoft};
  opacity: calc(${props => props.$alpha} * (0.55 + 0.45 * var(--audio-thumb-energy, 0)));
  transition: opacity 260ms ease;
`;

/**
 * ホバー時の光沢。tilt.js の glare と同じく、面全体に掛かる方向性のある光の帯を
 * ポインタの角度に合わせて回す。帯を要素より大きく取って中央部だけを覗かせる
 * ことで、どの角度でも切れ目のない滑らかな諧調になる。
 *
 * 地が白いため白い光だけでは変化が読めない。帯の反対側に僅かな陰を置いて
 * 「片側から光が当たっている」対比を作っている。
 */
const GlareLayer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: inherit;
  pointer-events: none;
  opacity: var(--glare-opacity, 0);
  transition: opacity 240ms ease;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 300%;
    height: 300%;
    transform-origin: 0 0;
    transform: rotate(var(--glare-angle, 0deg)) translate(-50%, -50%);
    /* 見えるのは帯の中央 3 割ほど。停止位置をそこに寄せないと諧調が出ない */
    background-image: linear-gradient(
      0deg,
      rgba(96, 110, 134, 0.2) 30%,
      rgba(255, 255, 255, 0) 50%,
      rgba(255, 255, 255, 0.95) 70%
    );
  }

  /* ポインタ直下のハイライト。ガラスが光を拾っている感じを足す */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse 40% 40% at var(--glare-x, 50%) var(--glare-y, 50%),
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }
`;

// =============================================================================
// メインコンポーネント
// =============================================================================

/** glowTrigger と現在の状態から、グローの基準不透明度を決める */
function resolveGlowAlpha(
  trigger: AudioThumbnailGlowTrigger,
  isPlaying: boolean,
  isHovering: boolean,
): number {
  switch (trigger) {
    case 'none':
      return 0;
    case 'always':
      return GLOW_ALPHA_ACTIVE;
    case 'hover':
      return isHovering ? GLOW_ALPHA_ACTIVE : GLOW_ALPHA_IDLE;
    case 'playing':
      return isPlaying ? GLOW_ALPHA_ACTIVE : GLOW_ALPHA_IDLE;
    case 'both':
    default:
      return isPlaying || isHovering ? GLOW_ALPHA_ACTIVE : GLOW_ALPHA_IDLE;
  }
}

const STATIC_DRIVE: DriveSignal = { level: 0, low: 0, mid: 0, high: 0, beat: 0 };

const AudioThumbnailCanvas: FC<AudioThumbnailCanvasProps> = ({
  category,
  seed,
  motion,
  isPlaying,
  isHovering,
  // ホバー中の光はグレアが担うので、内側のグローは再生中だけに割り当てる
  glowTrigger = 'playing',
  getCurrentTime,
  readSpectrum,
  onFirstVisible,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  const palette = useMemo(() => createAudioThumbnailPalette(category, seed), [category, seed]);
  const scene = useMemo(() => createMeshScene(palette, category, seed), [palette, category, seed]);
  const preset = MOTION_PRESETS[category];

  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // ドリフト位相は再生の開始・停止をまたいで連続させる（止めるたびに絵が飛ばない）
  const elapsedRef = useRef(0);
  const driverRef = useRef<AudioDriver | null>(null);

  // 毎フレーム参照する値。変化のたびにティッカーを貼り直さずに済むよう ref に逃がす
  const isPlayingRef = useRef(isPlaying);
  const getCurrentTimeRef = useRef(getCurrentTime);
  const readSpectrumRef = useRef(readSpectrum);
  const onFirstVisibleRef = useRef(onFirstVisible);
  isPlayingRef.current = isPlaying;
  getCurrentTimeRef.current = getCurrentTime;
  readSpectrumRef.current = readSpectrum;
  onFirstVisibleRef.current = onFirstVisible;

  if (driverRef.current === null) driverRef.current = createAudioDriver(motion);
  useEffect(() => {
    driverRef.current = createAudioDriver(motion);
  }, [motion]);

  // 実スペクトル用の駆動。解析が張れているフレームだけこちらの出力を使う。
  // 両方を毎フレーム更新するのは、切り替わった瞬間に片方の状態が古くて
  // 絵が飛ぶのを防ぐため（どちらも更新自体は軽い）。
  const spectrumDriverRef = useRef<AudioDriver | null>(null);
  if (spectrumDriverRef.current === null) {
    spectrumDriverRef.current = createSpectrumDriver(() => readSpectrumRef.current?.() ?? null);
  }

  // 画面外のサムネイルは描かない。フローティングバー再生中は一覧をスクロール
  // しても再生が続くので、可視判定なしだと見えていないタイルまで回り続ける。
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let notified = false;
    const observer = new IntersectionObserver(
      entries =>
        entries.forEach(entry => {
          setIsVisible(entry.isIntersecting);
          if (entry.isIntersecting && !notified) {
            notified = true;
            onFirstVisibleRef.current?.();
          }
        }),
      { threshold: 0.01 },
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  // 再生中とホバー中だけ動かす。それ以外は 1 枚描いて rAF を手放す
  const shouldAnimate = !prefersReducedMotion && isVisible && (isPlaying || isHovering);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const setEnergy = (value: number) => {
      rootRef.current?.style.setProperty('--audio-thumb-energy', value.toFixed(3));
    };

    if (!shouldAnimate) {
      renderMeshFrame(ctx, scene, elapsedRef.current, STATIC_DRIVE, preset);
      setEnergy(0);
      return;
    }

    let lastFrameMs = performance.now();

    return registerMeshTicker(nowMs => {
      const deltaMs = nowMs - lastFrameMs;
      if (deltaMs < MIN_FRAME_INTERVAL_MS) return;
      lastFrameMs = nowMs;

      const deltaSec = deltaMs / 1000;
      elapsedRef.current += deltaSec;

      const playing = isPlayingRef.current;
      const timeSec = getCurrentTimeRef.current();
      const peaksDrive =
        driverRef.current?.update(timeSec, deltaSec, playing) ?? STATIC_DRIVE;
      const spectrumDrive = spectrumDriverRef.current?.update(timeSec, deltaSec, playing);
      const hasSpectrum = playing && Boolean(readSpectrumRef.current?.());
      const drive = hasSpectrum && spectrumDrive ? spectrumDrive : peaksDrive;

      renderMeshFrame(ctx, scene, elapsedRef.current, drive, preset);
      setEnergy(drive.level);
    });
  }, [shouldAnimate, scene, preset]);

  // グレアは毎フレーム更新されるので、再レンダリングを挟まず CSS 変数を直接書く
  const glareRectRef = useRef<DOMRect | null>(null);

  const handleMouseEnter = useCallback(() => {
    glareRectRef.current = rootRef.current?.getBoundingClientRect() ?? null;
  }, []);

  const handleMouseMove = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const root = rootRef.current;
    // mousemove ごとに矩形を測るとレイアウトを読み直すので enter 時のものを使う
    const rect = glareRectRef.current ?? root?.getBoundingClientRect();
    if (!root || !rect || rect.width === 0 || rect.height === 0) return;

    const offsetX = (event.clientX - rect.left) / rect.width;
    const offsetY = (event.clientY - rect.top) / rect.height;
    const dx = offsetX - 0.5;
    const dy = offsetY - 0.5;

    // 光の帯をポインタの側へ向ける。+90 で帯の明るい端がポインタ方向を向く
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
    const distance = Math.min(1, Math.hypot(dx, dy) * 2);
    const opacity =
      GLARE_OPACITY_CENTER + (GLARE_OPACITY_EDGE - GLARE_OPACITY_CENTER) * distance;

    root.style.setProperty('--glare-angle', `${angle.toFixed(1)}deg`);
    root.style.setProperty('--glare-x', `${(offsetX * 100).toFixed(1)}%`);
    root.style.setProperty('--glare-y', `${(offsetY * 100).toFixed(1)}%`);
    root.style.setProperty('--glare-opacity', opacity.toFixed(3));
  }, []);

  // ポインタが出た通知は親の isHovering で受ける（自前の mouseleave では
  // ドラッグ中などに取りこぼす経路がある）
  useEffect(() => {
    if (isHovering) return;
    glareRectRef.current = null;
    rootRef.current?.style.setProperty('--glare-opacity', '0');
  }, [isHovering]);

  return (
    <Root
      ref={rootRef}
      aria-hidden
      data-audio-category={category}
      $surface={rgba(palette.surface, 1)}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
    >
      <MeshCanvas ref={canvasRef} width={MESH_CANVAS_SIZE} height={MESH_CANVAS_SIZE} />
      <GrainLayer />
      <EdgeRefractionLayer />
      <GlassLayer />
      <EdgeLayer $from={rgba(palette.edgeFrom, 0.95)} $to={rgba(palette.edgeTo, 0.95)} />
      <GlowLayer
        $glow={rgba(palette.glow, 0.55)}
        $glowSoft={rgba(palette.base, 0.22)}
        $alpha={resolveGlowAlpha(glowTrigger, isPlaying, isHovering)}
      />
      <GlareLayer />
    </Root>
  );
};

export default AudioThumbnailCanvas;
